import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowRight, CheckCircle, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';

const chatMessages = [
  { type: 'user', text: "Fais-moi un devis pour M. Dupont, renovation salle de bain" },
  { type: 'assistant', text: "Bien recu ! Je prepare le devis. Quel montant estimes-tu pour cette prestation ?" },
  { type: 'user', text: "Environ 4500€ HT" },
  { type: 'assistant', text: "Parfait. Le devis est pret et envoye a M. Dupont par email. Je te notifie des qu'il repond." },
  { type: 'user', text: "Relance les factures impayees stp" },
  { type: 'assistant', text: "C'est fait ! 3 relances envoyees : Martin (850€), Leroy (1200€), Garcia (620€). Je te tiens informe." },
];

export default function SuperAssistantSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="py-12 sm:py-16 md:py-24 bg-white overflow-hidden"
      data-testid="super-assistant-section"
    >
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-3 py-1.5 bg-ba-primary/10 text-ba-primary text-xs sm:text-sm font-medium rounded-full mb-3">
              Le coeur du systeme
            </span>
            
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-rm-text mb-3 sm:mb-4 leading-tight">
              Ton super assistant :{' '}
              <span className="text-gradient">le chef d'orchestre</span>{' '}
              de ton entreprise
            </h2>
            
            <div className="space-y-2 sm:space-y-3 text-rm-text-secondary text-sm sm:text-base">
              <p>
                Avec <strong className="text-rm-text">BatiAssist</strong>, tu bénéficies d'un <strong className="text-rm-text">Super Assistant à mi-temps ou plein temps</strong> pour ton entreprise.
              </p>
              <p>
                Cet assistant est recrute et forme pour comprendre ton activité.
              </p>
              <p>
                Tu communiques simplement avec lui via <strong className="text-rm-text">la messagerie BatiAssist</strong> integree a ton espace client.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-5 sm:mt-6"
            >
              <Link to="/super-assistant">
                <Button 
                  className="w-full sm:w-auto bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-6 py-4 sm:py-5 text-sm shadow-lg"
                  data-testid="super-assistant-cta"
                >
                  En savoir plus
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right - Messagerie BatiAssist (pas WhatsApp) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
              {/* Header messagerie */}
              <div className="bg-ba-primary px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Tchat BatiAssist</p>
                  <p className="text-xs text-white/70">Ton assistante Sophie</p>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-white/70">En ligne</span>
                </div>
              </div>

              {/* Messages */}
              <div className="p-3 sm:p-4 space-y-3 bg-slate-50 max-h-80 overflow-y-auto">
                {chatMessages.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                        msg.type === 'user' 
                          ? 'bg-ba-primary text-white rounded-br-sm' 
                          : 'bg-white text-rm-text border border-slate-200 rounded-bl-sm'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Input */}
              <div className="p-3 border-t border-slate-200 bg-white">
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    placeholder="Ecrivez ton message..." 
                    className="flex-1 bg-slate-100 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ba-primary/20"
                    disabled
                  />
                  <button className="w-10 h-10 bg-ba-secondary rounded-full flex items-center justify-center">
                    <Send className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Note */}
            <p className="text-center text-xs text-rm-text-secondary mt-3">
              Messagerie integree a ton espace client BatiAssist
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
